/**
 * Cấu hình API chung cho ứng dụng
 * Quản lý URL cơ sở của API dựa trên môi trường hiện tại
 */

/**
 * Lấy URL cơ sở của API dựa trên môi trường hiện tại
 * @returns {string} URL cơ sở của API
 */
export const getApiBase = (): string => {
  // Ưu tiên sử dụng environment variable từ .env hoặc deployment
  const envApiBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE;
  
  // Nếu có environment variable và không rỗng, sử dụng nó (ưu tiên cao nhất)
  if (envApiBase && envApiBase.trim() !== '') {
    return envApiBase;
  }
  
  // Auto-detect theo hostname/port
  if (typeof window !== 'undefined' && window.location) {
    const { hostname, port, protocol } = window.location;

    // 1) Production server: diendan247.com -> diendan247.com/api
    if (hostname === 'diendan247.com') {
      return 'https://diendan247.com/api';
    }

    // 2) Render: fe-backlink.onrender.com -> be-backlink.onrender.com
    if (/\.onrender\.com$/.test(hostname)) {
      // Mapping cụ thể nếu biết rõ domain
      if (hostname.startsWith('fe-backlink')) {
        return 'https://be-backlink.onrender.com';
      }
      // Generic mapping: frontend -> backend, fe- -> be-
      const mapped = hostname
        .replace('frontend', 'backend')
        .replace(/^fe-/, 'be-');
      return `https://${mapped}`;
    }

    // 3) Docker test environment: FE 7002 -> BE 7001
    if (port === '7002') return `http://${hostname}:7001`;

    // 4) Local development environments
    if (port === '3000') return `http://${hostname}:3001`;
    if (port === '3002') return `http://${hostname}:3001`;
    if (port === '6002') return `http://${hostname}:3001`;
    if (port === '8002') return `http://${hostname}:3001`;
    if (port === '9002') return `http://${hostname}:9101`;

    // 5) Docker development: FE 8002 -> BE 8001 (legacy)
    if (port === '8002') return `http://${hostname}:8001`;

    // Fallback: same host, switch 2 -> 1 if matches pattern, else default 3001
    const defaultUrl = protocol === 'https:' ? `https://${hostname}:3001` : `http://${hostname}:3001`;
    return defaultUrl;
  }

  // SSR fallback
  return 'http://localhost:3001';
};

/**
 * Lấy headers xác thực cho các request API
 * @returns Record<string, string> - Headers bao gồm Content-Type và Authorization
 */
export const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('access_token');
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

/**
 * Cấu hình API chung
 */
export const API_CONFIG = {
  baseUrl: getApiBase(),
  getAuthHeaders,
} as const;

/**
 * Chuyển một đường dẫn tương đối (ví dụ: uploads/avatars/xxx.png)
 * thành URL tuyệt đối dựa trên API base, đảm bảo luôn dùng HTTPS ở prod.
 */
export const toAbsoluteUrl = (input?: string | null): string => {
  const path = (input || '').trim();
  if (!path) return '';
  // Nếu là URL tuyệt đối
  if (/^https?:\/\//.test(path)) {
    try {
      const url = new URL(path);
      // Nếu trỏ về localhost/127.x và là file uploads, rewrite về cùng origin hiện tại
      if ((url.hostname === 'localhost' || /^127\./.test(url.hostname)) && /^\/uploads\//.test(url.pathname)) {
        if (typeof window !== 'undefined' && window.location) {
          return `${window.location.origin}${url.pathname}`;
        }
      }
    } catch {
      // Nếu URL không parse được, trả về nguyên bản
    }
    return path;
  }
  
  // Lấy base URL và đảm bảo có /uploads prefix cho static files
  let base = API_CONFIG.baseUrl;
  
  // Nếu path bắt đầu với uploads/, loại bỏ /api để truy cập static files
  if (path.startsWith('uploads/')) {
    // Loại bỏ /api để truy cập static files trực tiếp
    base = base.replace(/\/api$/, '');
    const result = `${base}/${path}`;
    return result;
  }
  
  // Cho các path khác, loại bỏ /api nếu có
  base = base.replace(/\/api$/, '');
  const result = `${base}/${path.replace(/^\//, '')}`;
  return result;
};